import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { requestRefresh } from '../../lib/resourceRefresh';
import type {
  AssistantActionCard,
  AssistantChange,
  AssistantCredits,
  AssistantMessageRead,
  AssistantStep,
  AssistantThreadDetail,
  AssistantThreadRead,
  PageContext,
  ProposalStatus,
  ProposalStatusUpdate,
} from '../../types/api';
import { FOLLOWUP_GIVE_UP_MS, isSettledCardError, parseActionEvent } from './actionCards';
import {
  ASSISTANT_DAILY_LIMIT,
  assistantEventsUrl,
  cancelAssistantAction,
  confirmAssistantAction,
  fetchAssistantThread,
  mintAssistantSseToken,
  sendAssistantMessage,
  setAssistantProposalStatus,
  undoAssistantAction,
} from './assistantApi';
import { creditsExhaustedMessage, recalledCredits, rememberCredits } from './assistantCredits';
import {
  isSettledUndoError,
  parseChangeEvent,
  refreshForChange,
  refreshForResource,
} from './changeRefresh';
import { markSentHere } from './clientActions';

/** Base gap before a stream reconnect, multiplied by the attempt number. */
const RECONNECT_DELAY_MS = 2000;

/** Reconnects before the stream is called lost and polling carries the wait alone. */
const MAX_RECONNECT_ATTEMPTS = 5;

/** Live chips kept for one reply: the newest few say what is happening now. */
const MAX_LIVE_STEPS = 8;

/** Poll cadence while a reply is outstanding and the stream is not open. */
const POLL_INTERVAL_MS = 2000;

/** Polls before giving up: five minutes at the interval above. */
const POLL_LIMIT = 150;

/** Poll cadence while a confirmed card's follow-up is expected any moment. */
const FOLLOWUP_POLL_MS = 4000;

/** Poll cadence while a confirmed card is only watching a run or a publish. */
const WATCH_POLL_MS = 20_000;

/** Health of the thread's event stream. The conversation can be fine while it is not. */
export type AssistantConnection = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'lost';

export interface SendOptions {
  pageContext?: PageContext;
  /**
   * A thread created for this very message. The hook may still be keyed on
   * "no thread" when the caller sends, so the target travels with the call.
   */
  thread?: AssistantThreadRead;
}

export interface AssistantThreadResult {
  thread: AssistantThreadRead | null;
  messages: AssistantMessageRead[];
  /** The server's word on whether a reply is still being written. */
  awaitingReply: boolean;
  /** Null until the thread has loaded once. */
  turnsLeftToday: number | null;
  isLoading: boolean;
  /** Load error only. `send` and `markProposal` throw, so callers keep input. */
  error: string | null;
  /** The thread is gone (deleted, or never this user's). */
  notFound: boolean;
  /** Tools the agent is running on the outstanding reply, in order. */
  liveSteps: AssistantStep[];
  /** Edits the outstanding reply has already committed, in order. */
  liveChanges: AssistantChange[];
  /** Cards the outstanding reply has already made, in order (read-only until it lands). */
  liveActions: AssistantActionCard[];
  /** A confirmed card is still following a run or a publish (server's word). */
  watching: boolean;
  /** A card was confirmed here and its follow-up has not landed yet (90 s at most). */
  expectingFollowup: boolean;
  /** The last expected follow-up did not land inside 90 s. */
  followupGaveUp: boolean;
  /**
   * This month's assistant credits (the account's), from the latest thread
   * read or, for a chat with no thread yet, the last one seen for this brand.
   * Shows 0 left once a send was refused for credits. Null = unknown.
   */
  credits: AssistantCredits | null;
  /** The credits are spent: the sentence to show instead of the composer's send. */
  creditsExhausted: string | null;
  connection: AssistantConnection;
  /** The reply never arrived inside the poll budget; polling has stopped. */
  pollTimedOut: boolean;
  /** Newest assistant row that landed after this thread was first loaded here. */
  latestArrivalId: string | null;
  send: (content: string, options?: SendOptions) => Promise<AssistantMessageRead>;
  markProposal: (
    messageId: string,
    proposalIndex: number,
    status: ProposalStatus,
    resourceId?: string,
  ) => Promise<AssistantMessageRead>;
  /**
   * Undoes one of a reply's changes and files the answer on the message.
   * Throws (the server's sentence) so the line can say why it did not.
   */
  undoChange: (messageId: string, actionId: string) => Promise<AssistantChange>;
  /**
   * Sets off one of a reply's cards and files the answer on the message; the
   * thread then expects a follow-up. Throws for the 404 and the two 409s.
   */
  confirmAction: (messageId: string, actionId: string) => Promise<AssistantActionCard>;
  /** Puts a card away unused. No follow-up. */
  cancelAction: (messageId: string, actionId: string) => Promise<AssistantActionCard>;
  refresh: () => void;
}

/** Last completed load, tagged with the (business, thread) it belongs to. */
interface LoadedThread {
  key: string;
  detail: AssistantThreadDetail | null;
  error: string | null;
  notFound: boolean;
  /** Message ids on the first load of this key: history, not arrivals. */
  baseline: ReadonlySet<string>;
}

interface LiveSteps {
  key: string;
  replyTo: string | null;
  steps: AssistantStep[];
  changes: AssistantChange[];
  actions: AssistantActionCard[];
}

/** A confirm made here, waiting on the follow-up turn it enqueued. */
interface FollowupExpectation {
  key: string;
  /** Distinguishes one confirm from the next, for the give-up timer. */
  id: number;
  /** Message ids on screen at confirm time: anything newer is the news. */
  known: ReadonlySet<string>;
  startedAt: number;
}

interface LiveConnection {
  key: string;
  connection: AssistantConnection;
}

const NO_MESSAGES: never[] = [];
const NO_STEPS: never[] = [];
const NO_CHANGES: never[] = [];
const NO_ACTIONS: never[] = [];

function threadKey(businessId: string, threadId: string): string {
  return `${businessId}:${threadId}`;
}

/** One SSE `step` frame, narrowed rather than cast: the frame is untrusted. */
function parseStep(data: unknown): (AssistantStep & { reply_to: string | null }) | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { tool, label, reply_to } = parsed as Record<string, unknown>;
    if (typeof label !== 'string' || !label) return null;
    return {
      tool: typeof tool === 'string' ? tool : '',
      label,
      reply_to: typeof reply_to === 'string' ? reply_to : null,
    };
  } catch {
    return null;
  }
}

function withMessage(
  detail: AssistantThreadDetail,
  message: AssistantMessageRead,
): AssistantThreadDetail {
  return {
    ...detail,
    messages: detail.messages.map((existing) => (existing.id === message.id ? message : existing)),
  };
}

/** The thread with one message's change replaced by the server's newer copy. */
function withChange(
  detail: AssistantThreadDetail,
  messageId: string,
  change: AssistantChange,
): AssistantThreadDetail {
  return {
    ...detail,
    messages: detail.messages.map((message) =>
      message.id !== messageId
        ? message
        : {
            ...message,
            changes: message.changes.map((existing) =>
              existing.action_id === change.action_id ? change : existing,
            ),
          },
    ),
  };
}

/** The thread with one message's card replaced by the server's newer copy. */
function withAction(
  detail: AssistantThreadDetail,
  messageId: string,
  card: AssistantActionCard,
): AssistantThreadDetail {
  return {
    ...detail,
    messages: detail.messages.map((message) =>
      message.id !== messageId
        ? message
        : {
            ...message,
            actions: message.actions.map((existing) =>
              existing.action_id === card.action_id ? card : existing,
            ),
          },
    ),
  };
}

/** A live list for one reply, with this card added or (same action) replaced. */
function withLiveAction(
  current: LiveSteps | null,
  key: string,
  replyTo: string | null,
  card: AssistantActionCard,
): LiveSteps {
  if (
    current === null ||
    current.key !== key ||
    (replyTo !== null && current.replyTo !== replyTo)
  ) {
    return { key, replyTo, steps: [], changes: [], actions: [card] };
  }
  const known = current.actions.some((existing) => existing.action_id === card.action_id);
  return {
    ...current,
    actions: known
      ? current.actions.map((existing) => (existing.action_id === card.action_id ? card : existing))
      : [...current.actions, card],
  };
}

/** A live list for one reply, with this change added or (same action) replaced. */
function withLiveChange(
  current: LiveSteps | null,
  key: string,
  replyTo: string | null,
  change: AssistantChange,
): LiveSteps {
  if (
    current === null ||
    current.key !== key ||
    (replyTo !== null && current.replyTo !== replyTo)
  ) {
    return { key, replyTo, steps: [], changes: [change], actions: [] };
  }
  const known = current.changes.some((existing) => existing.action_id === change.action_id);
  return {
    ...current,
    changes: known
      ? current.changes.map((existing) =>
          existing.action_id === change.action_id ? change : existing,
        )
      : [...current.changes, change],
  };
}

/**
 * One assistant conversation, by thread id.
 *
 * Load state is tagged with the (business, thread) it belongs to and the
 * visible state is derived during render, the same as `usePlanningChat` was, so
 * one brand's transcript can never flash under another's.
 *
 * The reply is written by a worker. While the server says `awaiting_reply`, the
 * hook listens to the thread's SSE stream on a **minted** short-lived token (the
 * session token never goes in a URL), exactly like `useGenerationProgress`: on
 * a drop the stream is closed and reopened on a fresh token with a linear
 * backoff. `step` frames build the live step chips; `change` frames (an edit
 * the agent just committed) build the live change lines and tell the open page
 * to re-read that record; `action` frames (a card made) build read-only live
 * cards; `message` and `failed` mean "re-read the thread". A reply can land
 * before the stream opens, so every open re-reads once. Whenever the stream is
 * not open, the thread is polled every 2 s (150 polls), so a broken stream
 * costs latency, never the reply.
 *
 * Follow-ups (turns the assistant takes on its own after a confirmed card) are
 * not user replies, so `awaiting_reply` never covers them. After a confirm here
 * the thread is polled every 4 s until a new follow-up row lands (90 s at
 * most), and every 20 s for as long as the server says `watching`. The dock
 * owns this hook even while its panel is shut, so an update still arrives and
 * lights the unread dot.
 */
export function useAssistantThread(
  businessId: string | null,
  threadId: string | null,
): AssistantThreadResult {
  const [loaded, setLoaded] = useState<LoadedThread | null>(null);
  const [live, setLive] = useState<LiveSteps | null>(null);
  const [stream, setStream] = useState<LiveConnection | null>(null);
  const [timedOutKey, setTimedOutKey] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [followup, setFollowup] = useState<FollowupExpectation | null>(null);
  const [gaveUpId, setGaveUpId] = useState<number | null>(null);
  // A send refused for credits, and the credits read it was refused against.
  const [refusedForCredits, setRefusedForCredits] = useState<{
    businessId: string;
    message: string;
    seen: AssistantCredits | null;
  } | null>(null);
  // Actions whose record the open page was already told to re-read.
  const refreshedActions = useRef(new Set<string>());
  const expectationCount = useRef(0);

  const key = businessId && threadId ? threadKey(businessId, threadId) : '';
  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  /** Files a fresh read, keeping the baseline of a thread already on screen. */
  const commit = useCallback((commitKey: string, detail: AssistantThreadDetail) => {
    setLoaded((current) => ({
      key: commitKey,
      detail,
      error: null,
      notFound: false,
      baseline:
        current?.key === commitKey
          ? current.baseline
          : new Set(detail.messages.map((message) => message.id)),
    }));
  }, []);

  useEffect(() => {
    if (!businessId || !threadId) return;

    const controller = new AbortController();
    let cancelled = false;
    const loadKey = threadKey(businessId, threadId);

    void (async () => {
      try {
        const detail = await fetchAssistantThread(businessId, threadId, controller.signal);
        if (cancelled) return;
        commit(loadKey, detail);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        const notFound = caught instanceof ApiError && caught.status === 404;
        setLoaded((current) =>
          // A failed re-read keeps the transcript already on screen.
          current?.key === loadKey && current.detail !== null && !notFound
            ? current
            : {
                key: loadKey,
                detail: null,
                error: notFound ? null : getErrorMessage(caught, 'Could not open this chat.'),
                notFound,
                baseline: new Set(),
              },
        );
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, threadId, nonce, commit]);

  const isCurrent = loaded !== null && loaded.key === key && key !== '';
  const detail = isCurrent ? loaded.detail : null;
  const awaitingReply = detail?.awaiting_reply ?? false;
  const pollTimedOut = timedOutKey === key && awaitingReply;
  const connection: AssistantConnection = !awaitingReply
    ? 'idle'
    : stream?.key === key
      ? stream.connection
      : 'connecting';

  // The stream: open only while a reply is outstanding.
  useEffect(() => {
    if (!businessId || !threadId || !awaitingReply) return;

    const streamKey = threadKey(businessId, threadId);
    let cancelled = false;
    let attempts = 0;
    // A `message` or `failed` frame ended this wait; the close that follows is not a drop.
    let settled = false;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function report(next: AssistantConnection) {
      setStream({ key: streamKey, connection: next });
    }

    function scheduleReconnect() {
      if (cancelled || settled) return;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        report('lost');
        return;
      }
      attempts += 1;
      report('reconnecting');
      timer = setTimeout(() => {
        timer = null;
        void open();
      }, RECONNECT_DELAY_MS * attempts);
    }

    async function open() {
      if (cancelled || !businessId || !threadId) return;

      let token: string;
      try {
        // A fresh token per attempt: the last one may have expired.
        token = (await mintAssistantSseToken(businessId, threadId)).token;
      } catch {
        scheduleReconnect();
        return;
      }
      if (cancelled) return;

      const events = new EventSource(assistantEventsUrl(threadId, token));
      source = events;

      function close() {
        events.close();
        if (source === events) source = null;
      }

      events.onopen = () => {
        attempts = 0;
        report('open');
        // The reply may have landed before this stream existed.
        refresh();
      };

      events.addEventListener('step', (event: Event) => {
        const step = parseStep((event as MessageEvent<unknown>).data);
        if (cancelled || step === null) return;
        attempts = 0;
        setLive((current) =>
          current !== null &&
          current.key === streamKey &&
          (step.reply_to === null || current.replyTo === step.reply_to)
            ? {
                ...current,
                steps: [...current.steps, { tool: step.tool, label: step.label }].slice(
                  -MAX_LIVE_STEPS,
                ),
              }
            : {
                key: streamKey,
                replyTo: step.reply_to,
                steps: [{ tool: step.tool, label: step.label }],
                changes: [],
                actions: [],
              },
        );
      });

      // An edit landed mid-turn: show it now, and let the page it lives on
      // re-read it now rather than when the reply is done.
      events.addEventListener('change', (event: Event) => {
        const frame = parseChangeEvent((event as MessageEvent<unknown>).data);
        if (cancelled || frame === null || !businessId) return;
        attempts = 0;
        if (!refreshedActions.current.has(frame.change.action_id)) {
          refreshedActions.current.add(frame.change.action_id);
          refreshForChange(businessId, frame.change);
        }
        setLive((current) => withLiveChange(current, streamKey, frame.replyTo, frame.change));
      });

      // A card was made: show it while the reply is still being written.
      events.addEventListener('action', (event: Event) => {
        const frame = parseActionEvent((event as MessageEvent<unknown>).data);
        if (cancelled || frame === null) return;
        attempts = 0;
        setLive((current) => withLiveAction(current, streamKey, frame.replyTo, frame.action));
      });

      // Both mean "the row is written", and the server closes the stream after
      // either: settle (no reconnect), re-read, and let the transcript say it.
      // If that read still says awaiting, the poll fallback carries the wait.
      for (const name of ['message', 'failed'] as const) {
        events.addEventListener(name, () => {
          if (cancelled) return;
          settled = true;
          close();
          report('idle');
          refresh();
        });
      }

      events.onerror = () => {
        if (settled) return;
        close();
        scheduleReconnect();
      };
    }

    void open();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      source?.close();
    };
  }, [businessId, threadId, awaitingReply, refresh]);

  // The fallback: poll while a reply is outstanding and the stream is not open.
  const shouldPoll = awaitingReply && connection !== 'open' && !pollTimedOut;

  useEffect(() => {
    if (!businessId || !threadId || !shouldPoll) return;

    const pollKey = threadKey(businessId, threadId);
    const controller = new AbortController();
    let cancelled = false;
    let timer: number | undefined;
    let polls = 0;

    function schedule() {
      timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    }

    async function poll() {
      if (cancelled || !businessId || !threadId) return;
      polls += 1;
      try {
        const next = await fetchAssistantThread(businessId, threadId, controller.signal);
        if (cancelled) return;
        commit(pollKey, next);
        if (!next.awaiting_reply) return;
      } catch {
        // A dropped poll says nothing about the reply. Spend a poll, not the wait.
        if (cancelled || controller.signal.aborted) return;
      }
      if (polls >= POLL_LIMIT) {
        setTimedOutKey(pollKey);
        return;
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [businessId, threadId, shouldPoll, commit]);

  const send = useCallback(
    async (content: string, options: SendOptions = {}): Promise<AssistantMessageRead> => {
      if (!businessId) throw new Error('No brand is active yet.');
      const target = options.thread?.id ?? threadId;
      if (!target) throw new Error('No chat is open.');

      let message: AssistantMessageRead;
      try {
        message = await sendAssistantMessage(businessId, target, {
          content,
          ...(options.pageContext ? { page_context: options.pageContext } : {}),
        });
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === 'ASSISTANT_CREDITS_EXHAUSTED') {
          const seen =
            loaded?.key === threadKey(businessId, target) ? (loaded.detail?.credits ?? null) : null;
          setRefusedForCredits({ businessId, message: caught.message, seen });
          // The next read brings the server's own count (and its reset date).
          if (target === threadId) refresh();
        }
        throw caught;
      }
      markSentHere(message.id);
      const sendKey = threadKey(businessId, target);

      setTimedOutKey(null);
      // A new wait starts with no word from the stream, not the last wait's.
      setStream(null);
      setLive({ key: sendKey, replyTo: message.id, steps: [], changes: [], actions: [] });
      setLoaded((current) => {
        const existing = current?.key === sendKey ? current.detail : null;
        const thread: AssistantThreadRead = existing?.thread ??
          options.thread ?? {
            id: target,
            business_id: businessId,
            title: null,
            created_at: message.created_at,
            updated_at: message.created_at,
          };
        const nextDetail: AssistantThreadDetail = {
          thread: { ...thread, updated_at: message.created_at },
          messages: [...(existing?.messages ?? []), message],
          // On file and handed to the worker; the next read confirms both.
          awaiting_reply: true,
          // Unchanged on send: the server counts a message when its reply lands
          // (a failed one never counts). A thread new to this hook has no count
          // yet; the next read brings one.
          turns_left_today: existing?.turns_left_today ?? ASSISTANT_DAILY_LIMIT,
          watching: existing?.watching ?? false,
          credits: existing?.credits ?? null,
        };
        return {
          key: sendKey,
          detail: nextDetail,
          error: null,
          notFound: false,
          baseline:
            current?.key === sendKey
              ? current.baseline
              : new Set(nextDetail.messages.map((m) => m.id)),
        };
      });
      return message;
    },
    [businessId, threadId, loaded, refresh],
  );

  const markProposal = useCallback(
    async (
      messageId: string,
      proposalIndex: number,
      status: ProposalStatus,
      resourceId?: string,
    ): Promise<AssistantMessageRead> => {
      if (!businessId || !threadId) throw new Error('No chat is open.');
      const body: ProposalStatusUpdate = { proposal_index: proposalIndex, status };
      if (resourceId !== undefined) body.resource_id = resourceId;

      const updated = await setAssistantProposalStatus(businessId, threadId, messageId, body);
      const markKey = threadKey(businessId, threadId);
      setLoaded((current) =>
        current?.key !== markKey || current.detail === null
          ? current
          : { ...current, detail: withMessage(current.detail, updated) },
      );
      return updated;
    },
    [businessId, threadId],
  );

  const undoChange = useCallback(
    async (messageId: string, actionId: string): Promise<AssistantChange> => {
      if (!businessId || !threadId) throw new Error('No chat is open.');
      const undoKey = threadKey(businessId, threadId);
      try {
        const change = await undoAssistantAction(businessId, actionId);
        setLoaded((current) =>
          current?.key !== undoKey || current.detail === null
            ? current
            : { ...current, detail: withChange(current.detail, messageId, change) },
        );
        refreshForChange(businessId, change);
        return change;
      } catch (caught) {
        // Undone elsewhere, changed again, past its window: the row on screen
        // is out of date, so re-read it. The line still says why.
        if (isSettledUndoError(caught)) refresh();
        throw caught;
      }
    },
    [businessId, threadId, refresh],
  );

  const confirmAction = useCallback(
    async (messageId: string, actionId: string): Promise<AssistantActionCard> => {
      if (!businessId || !threadId) throw new Error('No chat is open.');
      const actionKey = threadKey(businessId, threadId);
      // Snapshot before the call: the follow-up is whatever lands after it.
      const known = new Set(
        loaded?.key === actionKey && loaded.detail
          ? loaded.detail.messages.map((message) => message.id)
          : [],
      );
      try {
        const card = await confirmAssistantAction(businessId, actionId);
        setLoaded((current) =>
          current?.key !== actionKey || current.detail === null
            ? current
            : { ...current, detail: withAction(current.detail, messageId, card) },
        );
        const touched = card.result?.resource ?? card.resource;
        if (touched) refreshForResource(businessId, touched);
        // Done or failed, the server enqueues one follow-up turn about it.
        expectationCount.current += 1;
        setFollowup({ key: actionKey, id: expectationCount.current, known, startedAt: Date.now() });
        return card;
      } catch (caught) {
        if (isSettledCardError(caught)) refresh();
        throw caught;
      }
    },
    [businessId, threadId, loaded, refresh],
  );

  const cancelAction = useCallback(
    async (messageId: string, actionId: string): Promise<AssistantActionCard> => {
      if (!businessId || !threadId) throw new Error('No chat is open.');
      const actionKey = threadKey(businessId, threadId);
      try {
        const card = await cancelAssistantAction(businessId, actionId);
        setLoaded((current) =>
          current?.key !== actionKey || current.detail === null
            ? current
            : { ...current, detail: withAction(current.detail, messageId, card) },
        );
        return card;
      } catch (caught) {
        if (isSettledCardError(caught)) refresh();
        throw caught;
      }
    },
    [businessId, threadId, refresh],
  );

  // A reply that arrived while the stream was not open (polling, or a reload
  // mid-turn) never sent its `change` frames: tell the pages now. Rows from the
  // thread's first load are history, not news.
  const arrivalsDetail = isCurrent ? loaded.detail : null;
  const arrivalsBaseline = isCurrent ? loaded.baseline : null;
  useEffect(() => {
    if (!businessId || arrivalsDetail === null || arrivalsBaseline === null) return;
    for (const message of arrivalsDetail.messages) {
      if (message.role !== 'assistant' || arrivalsBaseline.has(message.id)) continue;
      // An update means a watched run or plan moved: let its screens re-read.
      const marker = `followup:${message.id}`;
      if (message.kind === 'followup' && !refreshedActions.current.has(marker)) {
        refreshedActions.current.add(marker);
        requestRefresh(businessId, ['runs', 'planned_posts']);
      }
      for (const change of message.changes) {
        if (change.status !== 'done' || refreshedActions.current.has(change.action_id)) continue;
        refreshedActions.current.add(change.action_id);
        refreshForChange(businessId, change);
      }
    }
  }, [businessId, arrivalsDetail, arrivalsBaseline]);

  const messages = detail?.messages ?? NO_MESSAGES;

  // Credits belong to the account: remember the latest read so a new chat
  // still has a meter.
  const readCredits = detail?.credits ?? null;
  useEffect(() => {
    if (businessId && readCredits) rememberCredits(businessId, readCredits);
  }, [businessId, readCredits]);

  const knownCredits = readCredits ?? recalledCredits(businessId);
  const refusal =
    refusedForCredits !== null && refusedForCredits.businessId === businessId
      ? refusedForCredits
      : null;
  // Spent when the server's count says so, or a send was refused and no newer
  // read has come in since.
  const serverSaysSpent = knownCredits !== null && knownCredits.left <= 0;
  const refusalStands = refusal !== null && (readCredits === null || readCredits === refusal.seen);
  const creditsExhausted =
    serverSaysSpent || refusalStands
      ? (refusal?.message ?? creditsExhaustedMessage(knownCredits))
      : null;
  const credits =
    knownCredits !== null && creditsExhausted !== null
      ? { ...knownCredits, left: 0 }
      : knownCredits;

  // The follow-up a confirm here promised: due until a new follow-up row is on
  // the thread, or 90 s have gone by.
  const expectation = followup !== null && followup.key === key ? followup : null;
  const followupLanded =
    expectation !== null &&
    messages.some(
      (message) =>
        message.role === 'assistant' &&
        message.kind === 'followup' &&
        !expectation.known.has(message.id),
    );
  const followupGaveUp = expectation !== null && !followupLanded && gaveUpId === expectation.id;
  const expectingFollowup = expectation !== null && !followupLanded && !followupGaveUp;
  const expectationId = expectingFollowup ? expectation.id : null;
  const expectationStartedAt = expectingFollowup ? expectation.startedAt : 0;

  useEffect(() => {
    if (expectationId === null) return;
    const left = Math.max(0, expectationStartedAt + FOLLOWUP_GIVE_UP_MS - Date.now());
    const timer = window.setTimeout(() => setGaveUpId(expectationId), left);
    return () => window.clearTimeout(timer);
  }, [expectationId, expectationStartedAt]);

  // Keep the thread fresh for updates while no user reply is outstanding (that
  // wait has its own stream and poll): quick while a follow-up is due, slow
  // while the server is only watching.
  const watching = detail?.watching ?? false;
  const watchInterval =
    !isCurrent || awaitingReply
      ? null
      : expectingFollowup
        ? FOLLOWUP_POLL_MS
        : watching
          ? WATCH_POLL_MS
          : null;

  useEffect(() => {
    if (!businessId || !threadId || watchInterval === null) return;

    const watchKey = threadKey(businessId, threadId);
    const controller = new AbortController();
    let cancelled = false;
    let timer: number | undefined;

    function schedule() {
      timer = window.setTimeout(() => void poll(), watchInterval ?? WATCH_POLL_MS);
    }

    async function poll() {
      if (cancelled || !businessId || !threadId) return;
      // A hidden tab waits; the next visible tick catches up.
      if (document.visibilityState !== 'hidden') {
        try {
          const next = await fetchAssistantThread(businessId, threadId, controller.signal);
          if (cancelled) return;
          commit(watchKey, next);
        } catch {
          if (cancelled || controller.signal.aborted) return;
        }
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [businessId, threadId, watchInterval, commit]);

  let latestArrivalId: string | null = null;
  if (isCurrent) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.role === 'assistant' && !loaded.baseline.has(message.id)) {
        latestArrivalId = message.id;
        break;
      }
    }
  }

  return {
    thread: detail?.thread ?? null,
    messages,
    awaitingReply,
    turnsLeftToday: detail?.turns_left_today ?? null,
    isLoading: key !== '' && !isCurrent,
    error: isCurrent ? loaded.error : null,
    notFound: isCurrent ? loaded.notFound : false,
    liveSteps: awaitingReply && live?.key === key ? live.steps : NO_STEPS,
    liveChanges: awaitingReply && live?.key === key ? live.changes : NO_CHANGES,
    liveActions: awaitingReply && live?.key === key ? live.actions : NO_ACTIONS,
    watching,
    expectingFollowup,
    followupGaveUp,
    credits,
    creditsExhausted,
    connection,
    pollTimedOut,
    latestArrivalId,
    send,
    markProposal,
    undoChange,
    confirmAction,
    cancelAction,
    refresh,
  };
}
